import { CC2MasterSheetDataRow } from "./sheets";

export type PMGradingConfig = {
  // From fetchPMSettings
  engineers: string[];
  faultCodes: Array<{
    site: string;
    clientCode: string;
    pmSheetId: string;
    reasons: string[];
  }>;
  reasonsForReturn: Array<{
    site: string;
    clientCode: string;
    pmSheetId: string;
    reasons: string[];
  }>;
  grades: string[];
  descriptionOfPackaging: string[];
  disposalIncompleteReason: string[];
  shippingServices: string[];
  maxDimensions: string[];

  // From fetchDataValidationSheet
  clients: string[];
  carriers: string[];
  auctioneers: string[];
  collectionPoints: string[];
  deliveryPoints: string[];
  warehousing: string[];
  refurbPartners: string[];
  randrCustomerReturnReason: string[];
  whoCausedReturn: string[];
  whoPaidForReturn: string[];
  clearcycleCollectionMethod: string[];
  randrRefundNoReturnReason: string[];
};
export interface PMGradingRowCatalogue {
  sku: string;
  clearCycleProductTitle: string;
  clientProductTitle: string;
  category: string;
  boxes?: number;
  totalKg?: number;
  totalCbm?: number;
  rrp: number;
  color: string;
  boxL?: number;
  boxW?: number;
  boxH?: number;
  boxKG?: number;
  description: string;
  ean: string;
  listingLink: string;
  images: string[];
  variablePfGirth?: number;
  shipping1ManService: string;
  variableCbm?: number;
  shipping2ManService: string;
  finalPackagingSize?: number;
}

export interface PMGradingRowCatalogueWithCircleMapping
  extends PMGradingRowCatalogue {
  circleMapping: {
    updatedTimestamp: string;
    id: string;
    createdTimestamp: string;
    circleStatus: string;
  };
}

export interface PMGradingRow {
  exit: string;
  pmid: string;
  manifest: {
    date: string;
    loadReference: string;
    clientCode: string;
    palletNumber: string;
  };
  catalogue: PMGradingRowCatalogue;
  inbounding: {
    quantityReceived: number | string;
    allBoxesPresent: string;
    dateTime: string;
    notes: string;
  };
  locationStatus: string;
  production: {
    doesProductMatchLink: string;
    suspectedReasonForReturn: string;
    assessmentFaultCode: string;
    engineer: string;
    listingComments: string;
    spareParts: string;
    productImages: string;
    grade: string;
    disposalReason: string;
    dateTime: string;
    postProductionExit: string;
    productionNumberOfBoxes: number | string;
    productionDescriptionOfPackaging: string;
    packagingShippingService: string;
    isShippingSuitable: string;
    outboundShippingService: string;
  };
  outbound: {
    palletNumber: string;
    postPackagingStockExit: string;
    date: string;
    despatchedTo: string;
    loadReference: string;
    mpSubmissionDatetime: string;
  };
  listing: {
    sku: string;
    preCreated: string;
    auctionConditionGrade: string;
    salesChannel: string;
    saleOrderNumber: string;
    dateOfSale: string;
    productDescription: string;
  };
  stockNotes: string;
  stockLocation: string;
  custom1: string;
  custom2: string;
  custom3: string;
  numberOfSeats: string;
}

/**
 * Event type for the CRAP (Catalogue Request for Amendment Pipeline) PUT request.
 * Mirrors the columns in the sheet.
 */
export interface CRAPPutRowEvent {
  /**
   * pmDocID is the unique identifier for the PM document.
   */
  pmDocID: string;

  /**
   * Email of the user who submitted the request.
   */
  userEmail: string;

  preCreated?: string;
  mpTransmission?: string;

  /**
   * Item SKU
   */
  sku: string;

  /**
   * Item title
   */
  title: string;

  /**
   * RRP (Recommended Retail Price) of the item.
   */
  rrp: number;

  /**
   * Weight of the item in kilograms.
   */
  totalKgs: number;

  /**
   * Shipping method for the item.
   */
  boxes: number;

  /**
   * Box height in centimeters.
   */
  boxH: number;

  /**
   * Box width in centimeters.
   */
  boxW: number;

  /**
   * Box depth in centimeters.
   */
  boxD: number;

  /**
   * Image URLs associated with the item.
   */
  imageUrls: string[];

  /**
   * Description of the item.
   */
  description: string;
}

export interface RetrieveConfigurationResult {
  configuration: PMGradingConfig;
  cc2Data: CC2MasterSheetDataRow[];
}

export const retrieveConfiguration = async (token: string) => {
  const apiUrl = process.env.REACT_APP_PMUI_CONFIGURATION_URL;
  if (!apiUrl) {
    throw new Error("PMUI_CONFIGURATION_URL is not set");
  }

  const config = await fetch(apiUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!config.ok) {
    throw new Error("Failed to retrieve configuration");
  }

  return (await config.json()) as RetrieveConfigurationResult;
};

export interface RetrievePMRowResult {
  pmGradingRow?: PMGradingRow;
  source?: "database" | "google-sheet";
  error?: string;
}

export const retrievePMRow = async (
  token: string,
  pmid: string,
  pmSheetId: string,
  skipDatabase: boolean = false
): Promise<RetrievePMRowResult> => {
  const apiUrl = process.env.REACT_APP_PMUI_GET_ROW_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_PMUI_GET_ROW_URL is not set");
  }

  let url = `${apiUrl}?pmid=${pmid}&sheetid=${pmSheetId}`;
  if (skipDatabase) {
    url += "&skipDatabase=true";
  }

  const pmRow = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!pmRow.ok) {
    if (pmRow.status === 404) {
      return {
        error: "Error: PM row not found",
      };
    }
    return {
      error: "Unknown error occurred",
    };
  }

  const result = await pmRow.json();

  return {
    pmGradingRow: result.pmGradingRow as PMGradingRow,
    source: result.source,
    error: undefined,
  };
};

export const putPMRow = async (
  token: string,
  pmid: string,
  pmSheetId: string,
  pmRow: PMGradingRow
) => {
  const apiUrl = process.env.REACT_APP_PMUI_PUT_ROW_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_PMUI_PUT_ROW_URL is not set");
  }

  const pmRowPutResult = await fetch(`${apiUrl}`, {
    method: "PUT",
    body: JSON.stringify({ pmid, pmSheetId, pmRow }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!pmRowPutResult.ok) {
    throw new Error("Failed to PUT PM row");
  }

  return await pmRowPutResult.json();
};

export const putCRAPRow = async (token: string, crapRow: CRAPPutRowEvent) => {
  const apiUrl = process.env.REACT_APP_PMUI_PUT_CRAP_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_PMUI_PUT_CRAP_URL is not set");
  }

  const pmRowPutResult = await fetch(`${apiUrl}`, {
    method: "PUT",
    body: JSON.stringify(crapRow),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!pmRowPutResult.ok) {
    throw new Error("Failed to PUT CRAP row");
  }

  return await pmRowPutResult.json();
};

export interface UpdateCatalogueAndGetSKUsResponse {
  items: PMGradingRowCatalogue[];
  uniqueSkusStored: number;
}
export const updateCatalogueAndGetSKUs = async (
  token: string,
  sheetId: string,
  skus: string[]
) => {
  const apiUrl = process.env.REACT_APP_PMUI_RETRIEVE_UPDATED_CC1_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_PMUI_RETRIEVE_UPDATED_CC1_URL is not set");
  }

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      pmSheetId: sheetId,
      skus: skus,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to update catalogue");
  }

  return (await response.json()) as UpdateCatalogueAndGetSKUsResponse;
};
